import { gql, useQuery } from '@apollo/client'
import { LoadingButton } from '@mapped/rivet/dist/mui/lab'
import {
  Button,
  Checkbox,
  TextField,
  Typography,
} from '@mapped/rivet/dist/mui/material'
import { styled } from '@mapped/rivet/dist/mui/styles'
import {
  Permission,
  PersonalAccessTokenIsPatNameAvailableResponse,
  User,
} from '@mapped/schema-graph-react-apollo'
import { pullAll } from 'lodash'
import { FunctionComponent, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { useNotifications } from '../../../../hooks/useNotifications'
import { EStepToken } from '../../../../types/general'
import { logger } from '../../../../utils/logger'
import { IScopesDescription } from './index'

export const Step2: FunctionComponent<IStep2Props> = ({
  setCurrentStep,
  createPersonalAccessToken,
  permissions,
  scopesDescription,
  user,
}) => {
  const notifications = useNotifications()

  const scopes = sortAndFilterScopes(permissions, scopesDescription)

  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [disabledScopes, setDisabledScopes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [displayError, setDisplayError] = useState(false)
  const [patName, setPatName] = useState('')
  const [debouncedPatName] = useDebounce(patName, 500)

  const patNameQuery = useQuery<
    { isPatNameAvailable: PersonalAccessTokenIsPatNameAvailableResponse },
    { name: string }
  >(IS_PAT_NAME_AVAILABLE_QUERY, {
    skip: !debouncedPatName,
    variables: {
      name: debouncedPatName,
    },
  })

  const isPatNameAvailable = patNameQuery?.data?.isPatNameAvailable?.isAvailable
  const isCheckingPatName = patNameQuery?.loading

  useEffect(() => {
    if (displayError) {
      notifications.push({
        type: 'error',
        message: 'Whoops! Something went wrong...',
      })
    }
  }, [displayError])

  function handleSelectScopes(
    scopeTarget: string,
    scopes: string[],
    disabled = false
  ) {
    if (disabled) {
      return false
    }

    const splitScope = scopeTarget.split('_')[0]
    let scopesToDisable: string[] = []

    if (scopeTarget.includes('READWRITE_ALL')) {
      // Disable All for this group
      scopesToDisable = scopes.filter(
        (s) => s.includes(splitScope) && s !== scopeTarget
      )
    } else if (scopeTarget.includes('READWRITE')) {
      // Disable _READ_ALL and _READ for this group
      scopesToDisable = scopes.filter(
        (s) =>
          s.includes(splitScope) &&
          !s.includes('READ_ALL') &&
          s !== scopeTarget &&
          !s.includes('READWRITE_ALL')
      )
    } else if (scopeTarget.includes('READALL')) {
      // Disable _READ for these groups
      scopesToDisable = scopes.filter(
        (s) =>
          s.includes(splitScope) &&
          s !== scopeTarget &&
          !s.includes('READWRITE')
      )
    }

    // Check if is already selected. If yes, deselect
    if (!selectedScopes.includes(scopeTarget)) {
      const finalScopes = [...selectedScopes, ...scopesToDisable]
      setSelectedScopes([...finalScopes, scopeTarget])

      const finalScopesDisabled = [...disabledScopes, ...scopesToDisable]
      return setDisabledScopes(finalScopesDisabled)
    } else {
      const finalScopes = pullAll(disabledScopes, scopesToDisable)

      setSelectedScopes([
        ...pullAll(
          selectedScopes.filter((scope) => scope !== scopeTarget),
          scopesToDisable
        ),
      ])

      return setDisabledScopes([...finalScopes])
    }
  }

  function handleSelectAllScopes() {
    if (selectedScopes.length === scopes.length) {
      setSelectedScopes([])
    } else {
      setSelectedScopes(scopes)
    }
    setDisabledScopes([])
  }

  const handleGenerateAccessToken = async () => {
    if (!isPatNameAvailable) {
      return
    }

    setIsLoading(true)

    try {
      await createPersonalAccessToken({
        variables: {
          input: {
            pat: {
              userId: user?.id,
              name: patName,
              permissions: selectedScopes,
            },
          },
        },

        refetchQueries: ['tokens'],
      })

      setPatName('')
      setSelectedScopes([])
      setCurrentStep(EStepToken.GENERATED)
    } catch (e: any) {
      logger.error(`[create PAT mutation error]`, e)
      setDisplayError(true)
    }

    setIsLoading(false)
  }

  function getScopeDescription(
    scope: string,
    scopesDescription: IScopesDescription[]
  ) {
    return scopesDescription.find((s) => s.title === scope)?.description
  }

  let currentSection = ''

  return (
    <div>
      <p>
        Personal access tokens allow you to access your own data via the API.{' '}
      </p>

      <Label>Token name</Label>

      <TextField
        name="patName"
        placeholder="Token name"
        required={true}
        disabled={!!isLoading}
        onChange={(e) => setPatName(e.target.value)}
        sx={{ width: '347px' }}
        error={isPatNameAvailable === false}
        helperText={
          isPatNameAvailable === false &&
          'This PAT name is taken. Choose a different one.'
        }
      />

      <SelectScopes>
        <strong>Select scopes</strong>
        <br />
        <span>Scopes define the access level for personal tokens</span>
      </SelectScopes>

      <ScopesTable className="scopes-table">
        <li
          style={{ cursor: 'pointer' }}
          key={`scope-list-all-0`}
          onClick={handleSelectAllScopes}
        >
          <Checkbox
            id="select-all"
            name="select-all"
            value="select-all"
            checked={selectedScopes.length === scopes.length}
            readOnly={true}
            sx={{ padding: 0, marginRight: '16px' }}
          />

          <span>Select All Scopes</span>
        </li>

        {(scopes as string[]).map((scope: string, index: number) => {
          const scopeSection = scope.split('.')[0]

          let addBorder = { borderTop: '1px solid #b8bfcc' }

          if (scopeSection === currentSection) {
            addBorder = { borderTop: '0' }
          }

          currentSection = scopeSection

          return (
            <li
              style={{ cursor: 'pointer', ...addBorder }}
              key={`scope-list-${index}`}
              onClick={() =>
                handleSelectScopes(
                  scope,
                  scopes,
                  disabledScopes.includes(scope)
                )
              }
            >
              <div>
                <Checkbox
                  id={`scope-list-${index}`}
                  name={`scope-list-${index}`}
                  value={`scope-list-${index}`}
                  checked={selectedScopes.includes(scope)}
                  disabled={disabledScopes.includes(scope)}
                  readOnly={true}
                  sx={{ padding: 0 }}
                />
              </div>

              <div style={{ flex: 1 }}>
                <span>{scope}</span>

                <Typography
                  sx={(theme) => ({
                    fontSize: '12px',
                    color: theme.palette.text.a50,
                  })}
                >
                  {getScopeDescription(scope, scopesDescription)}
                </Typography>
              </div>
            </li>
          )
        })}
      </ScopesTable>

      <ButtonsContainer className="buttons-container">
        <LoadingButton
          loading={isLoading}
          onClick={handleGenerateAccessToken}
          disabled={
            isCheckingPatName ||
            !isPatNameAvailable ||
            selectedScopes.length < 1
          }
        >
          Generate Access Token
        </LoadingButton>

        <Button
          variant="outlined"
          style={{ marginLeft: '12px' }}
          onClick={() => setCurrentStep(EStepToken.IDLE)}
        >
          Cancel
        </Button>
      </ButtonsContainer>
    </div>
  )
}

function sortAndFilterScopes(
  scopes: Permission[],
  scopesDescription: IScopesDescription[]
) {
  // Make sure list has the right order
  scopes.sort((a, b) => (a < b ? -1 : 1))

  /*
    Group by Category
    Example: User: [], DeviceProfile: []
  */
  let byCategory = {} as any

  scopes.map((item) => {
    const index = item.split('_')

    if (!byCategory[index[0]]) {
      byCategory[index[0]] = []
    }

    byCategory[index[0]].push(item)
  })

  // Convert Object in Array
  const finalScopes: string[][] = Object.values(byCategory)

  // Organize all ".All" at the end
  finalScopes.map((finalScope) => {
    const sortedItem = finalScope.sort((a: string, b: string) => {
      if (a.includes('_ALL')) {
        return 1
      }

      if (b.includes('_ALL')) {
        return -1
      }

      return 1
    })

    return sortedItem
  })

  return finalScopes
    .flat()
    .filter((scope) => !!scopesDescription.find((s) => s.title === scope))
}

const IS_PAT_NAME_AVAILABLE_QUERY = gql`
  query isPatNameAvailable($name: String) {
    isPatNameAvailable(name: $name) {
      isAvailable
    }
  }
`

const Label = styled.label`
  display: block;
  color: ${(props) => props.theme.palette.text.primary};
  font-size: 14px;
  line-height: 20px;
  margin-top: 32px;
  margin-bottom: 12px;
  font-weight: 500;
`

const SelectScopes = styled.div`
  margin-top: 32px;
  margin-bottom: 24px;
`

const ScopesTable = styled.ul`
  width: 100%;
  border: 1px solid #b8bfcc;

  & > li {
    display: flex;
    padding: 16px;
    font-size: 14px;
    align-items: center;

    > div:first-of-type {
      margin-right: 16px;
    }

    > div:last-of-type {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }

  & > li:last-child {
    border-bottom: 0;
  }
`

const ButtonsContainer = styled.div`
  margin-top: 36px;
`

export interface IStep2Props {
  setCurrentStep: (step: EStepToken) => void
  createPersonalAccessToken: (...args: any[]) => any
  permissions: Permission[]
  scopesDescription: IScopesDescription[]
  user?: User
}

export default Step2
